import React, { useState } from 'react';
import { Mail, Building, User, MessageSquare, Send, CheckCircle2, Quote, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { testimonials } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formType, setFormType] = useState('artist');
  const [artistForm, setArtistForm] = useState({
    name: '',
    email: '',
    genre: '',
    socialLinks: '',
    musicLink: '',
    message: ''
  });
  const [partnerForm, setPartnerForm] = useState({
    companyName: '',
    contactName: '',
    email: '',
    partnershipType: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleArtistSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const response = await fetch(`${API_URL}/api/contact/artist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(artistForm),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: data.message });
        setArtistForm({
          name: '',
          email: '',
          genre: '',
          socialLinks: '',
          musicLink: '',
          message: ''
        });
      } else {
        setSubmitStatus({ type: 'error', message: data.detail || 'Something went wrong. Please try again.' });
      }
    } catch {
      setSubmitStatus({ type: 'error', message: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const response = await fetch(`${API_URL}/api/contact/partner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(partnerForm),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: data.message });
        setPartnerForm({
          companyName: '',
          contactName: '',
          email: '',
          partnershipType: '',
          message: ''
        });
      } else {
        setSubmitStatus({ type: 'error', message: data.detail || 'Something went wrong. Please try again.' });
      }
    } catch {
      setSubmitStatus({ type: 'error', message: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 fade-in-section">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Mail className="text-white w-6 h-6" />
            <span className="text-white/60 text-sm tracking-widest uppercase">
              Get In Touch
            </span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6">
            Let's <span style={{ color: '#00A672' }}>Connect</span>
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            Whether you're an artist looking to join our roster or a partner interested 
            in collaboration, we'd love to hear from you.
          </p>
        </div>

        {/* Form Selection */}
        <div className="max-w-4xl mx-auto">
          <Tabs defaultValue="artist" className="w-full" onValueChange={setFormType}>
            <TabsList className="w-full grid grid-cols-2 bg-white/5 backdrop-blur-md p-1 rounded-2xl mb-12">
              <TabsTrigger 
                value="artist"
                className="rounded-xl py-4 data-[state=active]:bg-white data-[state=active]:text-black transition-all duration-300"
              >
                <User className="mr-2 w-5 h-5" />
                Artist Submission
              </TabsTrigger>
              <TabsTrigger 
                value="partner"
                className="rounded-xl py-4 data-[state=active]:bg-white data-[state=active]:text-black transition-all duration-300"
              >
                <Building className="mr-2 w-5 h-5" />
                B2B Partnership
              </TabsTrigger>
            </TabsList>

            {/* Artist Submission Form */}
            <TabsContent value="artist">
              <div className="bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-8 md:p-12">
                {submitStatus.type === 'success' && formType === 'artist' ? (
                  <div className="text-center py-12">
                    <CheckCircle2 size={64} className="text-green-400 mx-auto mb-6" />
                    <h3 className="text-3xl font-bold text-white mb-4">
                      Submission Received!
                    </h3>
                    <p className="text-white/70 text-lg">
                      {submitStatus.message}
                    </p>
                    <Button
                      onClick={() => setSubmitStatus({ type: null, message: '' })}
                      className="mt-6 bg-white/10 text-white hover:bg-white/20"
                    >
                      Submit Another
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleArtistSubmit} className="space-y-6">
                    {submitStatus.type === 'error' && formType === 'artist' && (
                      <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
                        <XCircle className="w-5 h-5 flex-shrink-0" />
                        <p>{submitStatus.message}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Artist / Band Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={artistForm.name}
                          onChange={(e) => setArtistForm({...artistForm, name: e.target.value})}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300"
                          placeholder="Your name"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          required
                          value={artistForm.email}
                          onChange={(e) => setArtistForm({...artistForm, email: e.target.value})}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300"
                          placeholder="your@email.com"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Genre *
                      </label>
                      <input
                        type="text"
                        required
                        value={artistForm.genre}
                        onChange={(e) => setArtistForm({...artistForm, genre: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300"
                        placeholder="e.g., Electronic, Hip-Hop, Indie"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Social Media Links
                      </label>
                      <input
                        type="text"
                        value={artistForm.socialLinks}
                        onChange={(e) => setArtistForm({...artistForm, socialLinks: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300"
                        placeholder="Instagram, Spotify, etc."
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Music / Demo Link *
                      </label>
                      <input
                        type="url"
                        required
                        value={artistForm.musicLink}
                        onChange={(e) => setArtistForm({...artistForm, musicLink: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300"
                        placeholder="SoundCloud, YouTube, Spotify link"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Tell Us About Your Music
                      </label>
                      <textarea
                        rows={5}
                        value={artistForm.message}
                        onChange={(e) => setArtistForm({...artistForm, message: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300 resize-none"
                        placeholder="What makes your sound unique? What are your goals?"
                        disabled={isSubmitting}
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-white text-black hover:bg-white/90 rounded-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Your Music
                          <Send className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </TabsContent>

            {/* B2B Partnership Form */}
            <TabsContent value="partner">
              <div className="bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-8 md:p-12">
                {submitStatus.type === 'success' && formType === 'partner' ? (
                  <div className="text-center py-12">
                    <CheckCircle2 size={64} className="text-green-400 mx-auto mb-6" />
                    <h3 className="text-3xl font-bold text-white mb-4">
                      Inquiry Received!
                    </h3>
                    <p className="text-white/70 text-lg">
                      {submitStatus.message}
                    </p>
                    <Button
                      onClick={() => setSubmitStatus({ type: null, message: '' })}
                      className="mt-6 bg-white/10 text-white hover:bg-white/20"
                    >
                      Submit Another
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handlePartnerSubmit} className="space-y-6">
                    {submitStatus.type === 'error' && formType === 'partner' && (
                      <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
                        <XCircle className="w-5 h-5 flex-shrink-0" />
                        <p>{submitStatus.message}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Company Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={partnerForm.companyName}
                          onChange={(e) => setPartnerForm({...partnerForm, companyName: e.target.value})}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300"
                          placeholder="Your company"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Contact Name *
                        </label>
                        <input
                          type="text"
                          required
                          value={partnerForm.contactName}
                          onChange={(e) => setPartnerForm({...partnerForm, contactName: e.target.value})}
                          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300"
                          placeholder="Your name"
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        required
                        value={partnerForm.email}
                        onChange={(e) => setPartnerForm({...partnerForm, email: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300"
                        placeholder="contact@company.com"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Partnership Type *
                      </label>
                      <select
                        required
                        value={partnerForm.partnershipType}
                        onChange={(e) => setPartnerForm({...partnerForm, partnershipType: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-white/30 transition-colors duration-300"
                        disabled={isSubmitting}
                      >
                        <option value="" className="bg-zinc-900">Select partnership type</option>
                        <option value="Radio Station" className="bg-zinc-900">Radio Station</option>
                        <option value="Festival / Venue" className="bg-zinc-900">Festival / Venue</option>
                        <option value="Influencer / Content Creator" className="bg-zinc-900">Influencer / Content Creator</option>
                        <option value="Brand Partnership" className="bg-zinc-900">Brand Partnership</option>
                        <option value="Other" className="bg-zinc-900">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Your Proposal *
                      </label>
                      <textarea
                        rows={5}
                        required
                        value={partnerForm.message}
                        onChange={(e) => setPartnerForm({...partnerForm, message: e.target.value})}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors duration-300 resize-none"
                        placeholder="Tell us about your partnership proposal..."
                        disabled={isSubmitting}
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-white text-black hover:bg-white/90 rounded-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          Submit Partnership Inquiry
                          <Send className="ml-2 w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Contact Info */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a 
              href="mailto:info@bekarmusicgroup.com"
              className="text-center p-6 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <MessageSquare className="w-8 h-8 text-white mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">General Inquiries</h3>
              <p className="text-white/60 text-sm">info@bekarmusicgroup.com</p>
            </a>
            <a 
              href="mailto:artists@bekarmusicgroup.com"
              className="text-center p-6 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <User className="w-8 h-8 text-white mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Artist Relations</h3>
              <p className="text-white/60 text-sm">artists@bekarmusicgroup.com</p>
            </a>
            <a 
              href="mailto:partnerships@bekarmusicgroup.com"
              className="text-center p-6 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <Building className="w-8 h-8 text-white mx-auto mb-3" />
              <h3 className="text-white font-semibold mb-2">Business Development</h3>
              <p className="text-white/60 text-sm">partnerships@bekarmusicgroup.com</p>
            </a>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mt-24 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Quote className="text-white w-5 h-5" />
              <span className="text-white/60 text-sm tracking-widest uppercase">
                Artist Voices
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What Artists Say
            </h2>
            <p className="text-lg text-white/60 max-w-2xl mx-auto">
              Hear from artists who've found their home at BEKÂR
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="relative group"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-8 h-full transition-all duration-500 hover:border-white/20 hover:scale-105">
                  <div className="mb-6">
                    <Quote size={36} className="text-white/20 group-hover:text-white/30 transition-colors duration-300" />
                  </div>
                  
                  <p className="text-white/90 text-base leading-relaxed mb-6 italic">
                    "{testimonial.quote}"
                  </p>
                  
                  <div className="border-t border-white/10 pt-6">
                    <p className="text-white font-semibold text-lg mb-1">
                      {testimonial.artist}
                    </p>
                    <p className="text-white/50 text-sm">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
